"use client"

import { Helmet } from 'react-helmet-async'
import { useState, useEffect, useRef } from "react"

const About = () => {
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || "We make events sawa"

  // For scroll animations
  const [isVisible, setIsVisible] = useState({
    overview: false,
    guidelines: false,
    cta: false,
    stats: false,
    testimonials: false,
    faq: false,
    features: false,
  })

  // For parallax effect
  const [scrollY, setScrollY] = useState(0)

  // For interactive elements
  const [activeTab, setActiveTab] = useState(0)
  const [activeFaq, setActiveFaq] = useState(null)

  // Refs for interactive elements
  const heroRef = useRef(null)

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      // Update scroll position for parallax
      setScrollY(window.scrollY)

      // Check which sections are visible
      const sections = {
        overview: document.getElementById("overview"),
        guidelines: document.getElementById("guidelines"),
        cta: document.getElementById("cta"),
        stats: document.getElementById("stats"),
        testimonials: document.getElementById("testimonials"),
        faq: document.getElementById("faq"),
        features: document.getElementById("features"),
      }

      Object.entries(sections).forEach(([key, section]) => {
        if (!section) return

        const rect = section.getBoundingClientRect()
        const isInView = rect.top <= window.innerHeight * 0.8

        setIsVisible((prev) => ({
          ...prev,
          [key]: isInView,
        }))
      })
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll() // Check on initial load

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Animation classes
  const getAnimationClass = (sectionName) => {
    return isVisible[sectionName]
      ? "opacity-100 translate-y-0 transition-all duration-1000 ease-out"
      : "opacity-0 translate-y-10 transition-all duration-1000 ease-out"
  }

  // Features data
  const features = [
    {
      title: "Event Creation",
      description: "Create beautiful event pages with customizable templates, ticketing options, and RSVP management.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      title: "Service Marketplace",
      description:
        "Connect with verified service providers for catering, venues, entertainment, photography, and more.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      title: "Guest Management",
      description: "Manage invitations, track RSVPs, assign seating, and communicate with attendees in real-time.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      title: "Budget Tracking",
      description: "Set budgets, track expenses, and manage payments to vendors all in one centralized dashboard.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ]

  // FAQ data
  const faqs = [
    {
      question: "How do I create an event on EventSawa?",
      answer:
        "Creating an event is simple! After signing up, click on 'Create Event' from your dashboard, fill in the event details, and publish. You can then manage RSVPs, find service providers, and track your event all in one place.",
    },
    {
      question: "How do service providers get paid?",
      answer:
        "EventSawa facilitates secure payments between event creators and service providers. Once a bid is accepted, the payment is held in escrow until the service is successfully delivered, ensuring protection for both parties.",
    },
    {
      question: "Is EventSawa available internationally?",
      answer:
        "Yes! EventSawa is available in over 50 countries worldwide. Our platform supports multiple currencies and languages to accommodate users from different regions.",
    },
    {
      question: "What types of events can I create?",
      answer:
        "EventSawa supports a wide range of events including weddings, corporate gatherings, conferences, birthday parties, fundraisers, and more. Our flexible platform can accommodate events of any size and type.",
    },
    {
      question: "How much does it cost to use EventSawa?",
      answer:
        "EventSawa offers a free basic plan for event creators with premium features available through subscription plans. Service providers can join for free and pay a small commission on successful bookings.",
    },
  ]

  return (
    <div className="bg-white min-h-screen overflow-hidden">
      <Helmet>
        <title>{eventTitle} - About Us</title>
        <meta name="description" content={eventDescription} />
        <style>
          {`
            @keyframes float {
              0% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
              100% { transform: translateY(0px); }
            }
            @keyframes float-reverse {
              0% { transform: translateY(0px) rotate(10deg); }
              50% { transform: translateY(-15px) rotate(5deg); }
              100% { transform: translateY(0px) rotate(10deg); }
            }
            @keyframes pulse-slow {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
            @keyframes spin-slow {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes bounce-slow {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-20px); }
            }
            .animate-float {
              animation: float 6s ease-in-out infinite;
            }
            .animate-float-reverse {
              animation: float-reverse 7s ease-in-out infinite;
            }
            .animate-pulse-slow {
              animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
            }
            .animate-spin-slow {
              animation: spin-slow 12s linear infinite;
            }
            .animate-bounce-slow {
              animation: bounce-slow 6s ease-in-out infinite;
            }
            .bg-pattern {
              background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.1'%3E%3Cpath d='M36 34c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4-4-1.8-4-4zM16 16c2.2 0 4 1.8 4 4s-1.8 4-4 4-4-1.8-4-4 1.8-4 4-4zm0 32c2.2 0 4 1.8 4 4s-1.8 4-4 4-4-1.8-4-4 1.8-4 4-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
            }
            .bg-dots {
              background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fillOpacity='0.05' fillRule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E");
            }
            .bg-circuit {
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 304 304' width='304' height='304'%3E%3Cpath fill='%23a78bfa' fillOpacity='0.1' d='M44.1 224a5 5 0 1 1 0 2H0v-2h44.1zm160 48a5 5 0 1 1 0 2H82v-2h122.1zm57.8-46a5 5 0 1 1 0-2H304v2h-42.1zm0 16a5 5 0 1 1 0-2H304v2h-42.1zm6.2-114a5 5 0 1 1 0 2h-86.2a5 5 0 1 1 0-2h86.2zm-256-48a5 5 0 1 1 0 2H0v-2h12.1zm185.8 34a5 5 0 1 1 0-2h86.2a5 5 0 1 1 0 2h-86.2zM258 12.1a5 5 0 1 1-2 0V0h2v12.1zm-64 208a5 5 0 1 1-2 0v-54.2a5 5 0 1 1 2 0v54.2zm48-198.2V80h62v2h-64V21.9a5 5 0 1 1 2 0zm16 16V64h46v2h-48V37.9a5 5 0 1 1 2 0zm-128 96V208h16v12.1a5 5 0 1 1-2 0V210h-16v-76.1a5 5 0 1 1 2 0zm-5.9-21.9a5 5 0 1 1 0 2H114v48H85.9a5 5 0 1 1 0-2H112v-48h12.1zm-6.2 130a5 5 0 1 1 0-2H176v-74.1a5 5 0 1 1 2 0V242h-60.1zm-16-64a5 5 0 1 1 0-2H114v48h10.1a5 5 0 1 1 0 2H112v-48h-10.1zM66 284.1a5 5 0 1 1-2 0V274H50v30h-2v-32h18v12.1zM236.1 176a5 5 0 1 1 0 2H226v94h48v32h-2v-30h-48v-98h12.1zm25.8-30a5 5 0 1 1 0-2H274v44.1a5 5 0 1 1-2 0V146h-10.1zm-64 96a5 5 0 1 1 0-2H208v-80h16v-14h-42.1a5 5 0 1 1 0-2H226v18h-16v80h-12.1zm86.2-210a5 5 0 1 1 0 2H272V0h2v32h10.1zM98 101.9V146H53.9a5 5 0 1 1 0-2H96v-42.1a5 5 0 1 1 2 0zM53.9 34a5 5 0 1 1 0-2H80V0h2v34H53.9zm60.1 3.9V66H82v64H69.9a5 5 0 1 1 0-2H80V64h32V37.9a5 5 0 1 1 2 0zM101.9 82a5 5 0 1 1 0-2H128V37.9a5 5 0 1 1 2 0V82h-28.1zm16-64a5 5 0 1 1 0-2H146v44.1a5 5 0 1 1-2 0V18h-26.1zm102.2 270a5 5 0 1 1 0 2H98v14h-2v-16h124.1zM242 149.9V160h16v34h-16v62h48v48h-2v-46h-48v-66h16v-30h-16v-12.1a5 5 0 1 1 2 0zM53.9 18a5 5 0 1 1 0-2H64V2H48V0h18v18H53.9zm112 32a5 5 0 1 1 0-2H192V0h50v2h-48v48h-28.1zm-48-48a5 5 0 0 1-9.8-2h2.07a3 3 0 1 0 5.66 0H178v34h-18V21.9a5 5 0 1 1 2 0V32h14V2h-58.1zm0 96a5 5 0 1 1 0-2H137l32-32h39V21.9a5 5 0 1 1 2 0V66h-40.17l-32 32H117.9zm28.1 90.1a5 5 0 1 1-2 0v-76.51L175.59 80H224V21.9a5 5 0 1 1 2 0V82h-49.59L146 112.41v75.69zm16 32a5 5 0 1 1-2 0v-99.51L184.59 96H300.1a5 5 0 0 1 3.9-3.9v2.07a3 3 0 0 0 0 5.66v2.07a5 5 0 0 1-3.9-3.9H185.41L162 121.41v98.69zm-144-64a5 5 0 1 1-2 0v-3.51l48-48V48h32V0h2v50H66v55.41l-48 48v2.69zM50 53.9v43.51l-48 48V208h26.1a5 5 0 1 1 0 2H0v-65.41l48-48V53.9a5 5 0 1 1 2 0zm-16 16V89.41l-34 34v-2.82l32-32V69.9a5 5 0 1 1 2 0zM12.1 32a5 5 0 1 1 0 2H9.41L0 43.41V40.6L8.59 32h3.51zm265.8 18a5 5 0 1 1 0-2h18.69l7.41-7.41v2.82L297.41 50H277.9zm-16 160a5 5 0 1 1 0-2H288v-71.41l16-16v2.82l-14 14V210h-28.1zm-208 32a5 5 0 1 1 0-2H64v-22.59L40.59 194H21.9a5 5 0 1 1 0-2H41.41L66 216.59V242H53.9zm150.2 14a5 5 0 1 1 0 2H96v-56.6L56.6 162H37.9a5 5 0 1 1 0-2h19.5L98 200.6V256h106.1zm-150.2 2a5 5 0 1 1 0-2H80v-46.59L48.59 178H21.9a5 5 0 1 1 0-2H49.41L82 208.59V258H53.9zM34 39.8v1.61L9.41 66H0v-2h8.59L32 40.59V0h2v39.8zM2 300.1a5 5 0 0 1 3.9 3.9H3.83A3 3 0 0 0 0 302.17V256h18v48h-2v-46H2v42.1zM34 241v63h-2v-62H0v-2h34v1zM17 18H0v-2h16V0h2v18h-1zm273-2h14v2h-16V0h2v16zm-32 273v15h-2v-14h-14v14h-2v-16h18v1zM0 92.1A5.02 5.02 0 0 1 6 97a5 5 0 0 1-6 4.9v-2.07a3 3 0 1 0 0-5.66V92.1zM80 272h2v32h-2v-32zm37.9 32h-2.07a3 3 0 0 0-5.66 0h-2.07a5 5 0 0 1 9.8 0zM5.9 0A5.02 5.02 0 0 1 0 5.9V3.83A3 3 0 0 0 3.83 0H5.9zm294.2 0h2.07A3 3 0 0 0 304 3.83V5.9a5 5 0 0 1-3.9-5.9zm3.9 300.1v2.07a3 3 0 0 0-1.83 1.83h-2.07a5 5 0 0 1 3.9-3.9zM97 100a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-48 32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 48a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-64a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 96a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-144a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-96 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm96 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-64a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-32 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM49 36a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-32 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM33 68a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-48a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 240a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-64a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm80-176a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 48a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm112 176a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-16 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM17 180a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0-32a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM17 84a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm32 64a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm16-16a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'%3E%3C/path%3E%3C/svg%3E");
            }
            .text-shadow {
              text-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .text-shadow-lg {
              text-shadow: 0 4px 8px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08);
            }
            .card-hover {
              transition: all 0.3s ease;
            }
            .card-hover:hover {
              transform: translateY(-5px);
              box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            }
            .gradient-text {
              background: linear-gradient(to right, #8b5cf6, #6366f1);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
            .clip-path-slant {
              clip-path: polygon(0 0, 100% 0, 100% 85%, 0 100%);
            }
            .clip-path-wave {
              clip-path: polygon(0 0, 100% 0, 100% 85%, 75% 90%, 50% 85%, 25% 90%, 0 85%);
            }
            .glass-effect {
              backdrop-filter: blur(10px);
              background: rgba(255, 255, 255, 0.1);
              border: 1px solid rgba(255, 255, 255, 0.2);
            }
            .tab-active {
              border-bottom: 2px solid #6366f1;
              color: #6366f1;
            }
            .tab-inactive {
              border-bottom: 2px solid transparent;
              color: #6b7280;
            }
          `}
        </style>
      </Helmet>

      {/* Hero Section */}
      <div className="relative overflow-hidden clip-path-slant" ref={heroRef} style={{ minHeight: "90vh" }}>
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-700 via-indigo-700 to-indigo-900"></div>

        {/* Animated Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Large circle */}
          <div
            className="absolute top-1/4 -left-20 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-float"
            style={{ animationDelay: "0s" }}
          ></div>

          {/* Medium circle */}
          <div
            className="absolute bottom-1/3 right-10 w-48 h-48 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-float-reverse"
            style={{ animationDelay: "1s" }}
          ></div>

          {/* Small circle */}
          <div
            className="absolute top-1/3 right-1/4 w-32 h-32 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-float"
            style={{ animationDelay: "2s" }}
          ></div>

          {/* Decorative elements */}
          <div className="absolute top-20 left-1/4 w-64 h-64 border-4 border-white border-opacity-10 rounded-full animate-spin-slow"></div>
          <div
            className="absolute bottom-20 right-1/4 w-40 h-40 border-4 border-white border-opacity-10 rounded-full animate-spin-slow"
            style={{ animationDirection: "reverse" }}
          ></div>
        </div>

        {/* Content */}
        <div
          className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8 flex flex-col items-center justify-center"
          style={{ minHeight: "80vh" }}
        >
          <span className="inline-block px-4 py-1.5 text-sm font-medium bg-white bg-opacity-20 text-white rounded-full mb-6 backdrop-blur-sm shadow-lg">
            Welcome to our platform
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold tracking-tight text-white mb-6 text-shadow-lg text-center leading-tight">
            Reimagining <span className="text-indigo-200">Event Management</span> with {eventTitle}
          </h1>

          <p className="text-xl max-w-3xl mx-auto text-white text-opacity-90 leading-relaxed text-center mb-10">
            We're on a mission to make event planning seamless and connect event creators with quality service providers
            through our innovative platform.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#overview"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-indigo-700 bg-white hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Learn More
              <svg
                className="ml-2 -mr-1 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                  transform="rotate(180 10 10)"
                />
              </svg>
            </a>

            <a
              href="/register"
              className="inline-flex items-center px-6 py-3 border border-white border-opacity-30 text-base font-medium rounded-full text-white bg-white bg-opacity-10 hover:bg-opacity-20 transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Get Started
              <svg
                className="ml-2 -mr-1 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce-slow">
            <svg
              className="w-10 h-10 text-white text-opacity-70"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        {/* Platform Overview */}
        <div id="overview" className={`mb-32 ${getAnimationClass("overview")}`}>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 text-sm font-medium bg-indigo-100 text-indigo-800 rounded-full mb-3">
              Our Platform
            </span>
            <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl mb-6 gradient-text">
              What is {eventTitle}?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {eventTitle} is a comprehensive event management platform designed to simplify the entire event planning
              process. We connect event creators with service providers, making it easy to organize successful events of
              any size.
            </p>
          </div>

          {/* Feature Tabs */}
          <div id="features" className={`mb-16 ${getAnimationClass("features")}`}>
            <div className="border-b border-gray-200 mb-8">
              <div className="flex overflow-x-auto space-x-8 py-2 justify-center">
                {features.map((feature, index) => (
                  <button
                    key={index}
                    className={`py-4 px-1 font-medium text-lg whitespace-nowrap transition-all duration-300 ${activeTab === index ? "tab-active" : "tab-inactive hover:text-gray-900"
                      }`}
                    onClick={() => setActiveTab(index)}
                  >
                    {feature.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 transition-all duration-500">
              <div className="grid md:grid-cols-5 gap-8 items-center">
                <div className="md:col-span-2 flex justify-center">
                  <div className="w-32 h-32 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 transform transition-all duration-500">
                    {features[activeTab].icon}
                  </div>
                </div>
                <div className="md:col-span-3">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{features[activeTab].title}</h3>
                  <p className="text-gray-600 text-lg mb-6">{features[activeTab].description}</p>
                  <a
                    href="#"
                    className="text-indigo-600 font-medium flex items-center hover:text-indigo-800 transition-colors"
                  >
                    Learn more
                    <svg
                      className="ml-2 w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      ></path>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-10 mt-16">
            {/* For Event Creators */}
            <div className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 card-hover relative overflow-hidden">


              <div className="relative">
                <div className="w-20 h-20 bg-indigo-100 rounded-2xl flex items-center justify-center mb-8">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-indigo-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">For Event Creators</h3>
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  Create and manage your events with ease. Our platform provides powerful tools to streamline your event
                  planning process from start to finish.
                </p>
                <ul className="space-y-5 text-gray-600">
                  {[
                    "Create customized event pages with beautiful templates",
                    "Manage guest lists and RSVPs with automated tools",
                    "Find and hire qualified service providers through our network",
                    "Track budgets and expenses with detailed analytics",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                        <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10">
                  <a
                    href="/creators"
                    className="inline-flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors"
                  >
                    Learn more about creator features
                    <svg
                      className="ml-2 w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      ></path>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* For Service Providers */}
            <div className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 card-hover relative overflow-hidden">


              <div className="relative">
                <div className="w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center mb-8">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">For Service Providers</h3>
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  Showcase your services and grow your business. Our platform connects you with clients looking for your
                  expertise and helps you manage your bookings efficiently.
                </p>
                <ul className="space-y-5 text-gray-600">
                  {[
                    "Create a professional profile highlighting your unique services",
                    "Bid on relevant event opportunities matching your expertise",
                    "Communicate directly with event organizers through our platform",
                    "Build your reputation through verified reviews and ratings",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                        <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10">
                  <a
                    href="/providers"
                    className="inline-flex items-center text-purple-600 font-medium hover:text-purple-800 transition-colors"
                  >
                    Learn more about provider features
                    <svg
                      className="ml-2 w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      ></path>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div id="stats" className={`mb-32 ${getAnimationClass("stats")}`}>
          <div className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-3xl p-12 overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 bg-pattern opacity-10"></div>

            <div className="relative">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 text-sm font-medium bg-white bg-opacity-20 text-white rounded-full mb-3">
                  Our Impact
                </span>
                <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-6 text-shadow">
                  Trusted by event planners worldwide
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { value: "10,000+", label: "Events Created" },
                  { value: "5,000+", label: "Service Providers" },
                  { value: "98%", label: "Satisfaction Rate" },
                  { value: "50+", label: "Countries" },
                ].map((stat, index) => (
                  <div
                    key={index}
                    className="text-center bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-20 transform transition-all duration-300 hover:scale-105"
                  >
                    <p
                      className="text-4xl font-extrabold text-white animate-pulse-slow"
                      style={{ animationDelay: `${index * 0.5}s` }}
                    >
                      {stat.value}
                    </p>
                    <p className="text-indigo-100 mt-2 font-medium">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Community Guidelines */}
        <div id="guidelines" className={`mb-32 ${getAnimationClass("guidelines")}`}>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 text-sm font-medium bg-indigo-100 text-indigo-800 rounded-full mb-3">
              Our Values
            </span>
            <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl mb-6 gradient-text">
              Community Guidelines
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              At {eventTitle}, we believe in fostering a respectful and professional community. Here's what we expect
              from our users:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {/* Respect */}
            <div className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 card-hover relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 bg-blue-500 rounded-t-2xl"></div>
              <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mb-8 mx-auto">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Mutual Respect</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We expect all users to treat each other with respect and professionalism. Our community thrives on
                positive interactions, constructive feedback, and inclusive behavior. Discrimination, harassment, or
                abusive behavior will not be tolerated.
              </p>
            </div>

            {/* Quality Service */}
            <div className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 card-hover relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 bg-green-500 rounded-t-2xl"></div>
              <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mb-8 mx-auto">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Quality Service</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Service providers are expected to deliver high-quality services as promised. This includes meeting
                deadlines, maintaining professional standards, and ensuring client satisfaction. We believe in
                excellence and accountability in all interactions.
              </p>
            </div>

            {/* Safety Measures */}
            <div className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 card-hover relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 bg-red-500 rounded-t-2xl"></div>
              <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mb-8 mx-auto">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Safety Measures</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Users should take appropriate precautionary measures when organizing or providing services. This
                includes verifying identities, meeting in public places for initial consultations, and following
                established safety protocols to ensure a secure experience for everyone.
              </p>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div id="testimonials" className={`mb-32 ${getAnimationClass("testimonials")}`}>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 text-sm font-medium bg-indigo-100 text-indigo-800 rounded-full mb-3">
              Testimonials
            </span>
            <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl mb-6 gradient-text">What Our Users Say</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                quote:
                  "EventSawa transformed how I plan corporate events. The platform is intuitive and connecting with quality vendors has never been easier. I've saved countless hours and delivered better experiences for our attendees.",
                author: "Sarah Johnson",
                role: "Corporate Event Planner",
                company: "Global Innovations Inc.",
                image: "https://randomuser.me/api/portraits/women/32.jpg",
              },
              {
                quote:
                  "As a caterer, I've grown my business by 40% since joining. The platform makes it simple to showcase my services and connect with clients who are looking for exactly what I offer. The bidding system is fair and transparent.",
                author: "Michael Chen",
                role: "Professional Caterer",
                company: "Delicious Bites Catering",
                image: "https://randomuser.me/api/portraits/men/46.jpg",
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 card-hover relative overflow-hidden"
              >


                <div className="relative">
                  <svg
                    className="h-16 w-16 text-indigo-200 mb-6"
                    fill="currentColor"
                    viewBox="0 0 32 32"
                    aria-hidden="true"
                  >
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                  <p className="text-xl text-gray-600 mb-8 italic leading-relaxed">{testimonial.quote}</p>
                  <div className="flex items-center">
                    <img
                      src={testimonial.image || "/placeholder.svg"}
                      alt={testimonial.author}
                      className="h-14 w-14 rounded-full object-cover border-2 border-indigo-100"
                    />
                    <div className="ml-4">
                      <p className="font-bold text-gray-900 text-lg">{testimonial.author}</p>
                      <p className="text-gray-600">{testimonial.role}</p>
                      <p className="text-indigo-600 text-sm">{testimonial.company}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="/testimonials"
              className="inline-flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors"
            >
              View more success stories
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </a>
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq" className={`mb-32 ${getAnimationClass("faq")}`}>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 text-sm font-medium bg-indigo-100 text-indigo-800 rounded-full mb-3">
              FAQ
            </span>
            <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl mb-6 gradient-text">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Get answers to the most common questions about using {eventTitle} for your events.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="mb-6 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-all duration-300"
              >
                <button
                  className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none"
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                >
                  <span className="font-medium text-lg text-gray-900">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-indigo-600 transform transition-transform duration-300 ${activeFaq === index ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                <div
                  className={`px-6 pb-5 transition-all duration-300 overflow-hidden ${activeFaq === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="/faq"
              className="inline-flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors"
            >
              View all FAQs
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </a>
          </div>
        </div>

        {/* Call to Action */}
        <div id="cta" className={`${getAnimationClass("cta")}`}>
          <div className="relative bg-gradient-to-r from-indigo-600 to-purple-700 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-circuit"></div>
            <div className="relative py-16 px-8 text-center">
              <h2 className="text-4xl font-extrabold text-white sm:text-5xl mb-6 text-shadow">
                Ready to make your events sawa?
              </h2>
              <p className="text-xl text-indigo-100 mb-10 max-w-2xl mx-auto">
                Join our community of event creators and service providers today and experience the difference.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a
                  href="/register"
                  className="inline-block px-8 py-4 bg-white text-indigo-700 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Sign up for free
                </a>
                <a
                  href="/events"
                  className="inline-block px-8 py-4 bg-indigo-500 bg-opacity-30 text-white font-bold rounded-xl border border-white border-opacity-30 hover:bg-opacity-40 transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Explore events
                </a>
              </div>

              {/* Decorative elements */}
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-float"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-float-reverse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white mt-16">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/about" className="text-gray-300 hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/careers" className="text-gray-300 hover:text-white transition-colors">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="/blog" className="text-gray-300 hover:text-white transition-colors">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="/press" className="text-gray-300 hover:text-white transition-colors">
                    Press
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/features" className="text-gray-300 hover:text-white transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <a href="/pricing" className="text-gray-300 hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="/security" className="text-gray-300 hover:text-white transition-colors">
                    Security
                  </a>
                </li>
                <li>
                  <a href="/faq" className="text-gray-300 hover:text-white transition-colors">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/help" className="text-gray-300 hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                <li>
                  <a href="/guides" className="text-gray-300 hover:text-white transition-colors">
                    Guides
                  </a>
                </li>
                <li>
                  <a href="/partners" className="text-gray-300 hover:text-white transition-colors">
                    Partners
                  </a>
                </li>
                <li>
                  <a href="/webinars" className="text-gray-300 hover:text-white transition-colors">
                    Webinars
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/contact" className="text-gray-300 hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
                <li>
                  <a href="/support" className="text-gray-300 hover:text-white transition-colors">
                    Support
                  </a>
                </li>
                <li>
                  <a href="/sales" className="text-gray-300 hover:text-white transition-colors">
                    Sales
                  </a>
                </li>
                <li>
                  <a href="/feedback" className="text-gray-300 hover:text-white transition-colors">
                    Feedback
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 mt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-400">
                © {new Date().getFullYear()} {eventTitle}. All rights reserved.
              </p>
            </div>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">Facebook</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">Instagram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default About
